import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import {
	Box,
	Button,
	TextField,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography,
} from '@mui/material';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import {
	AI_MODEL_FILE_LOADING_KEY,
	uploadAIModelFile,
	UploadAIModelFileValidationSchema,
	IUploadAIModelFileForm,
	IUploadAIModelFileModalProps,
} from 'src/features/ai_model_file';
import { AppDispatch, RootState } from 'src/store';
import { getFileHasher, terminateFileHasher } from 'src/utils/fileHashUtils';

const UploadAIModelFileModal: React.FC<IUploadAIModelFileModalProps> = ({
	isOpen,
	aiModelId,
	onClose,
	onSuccess,
}) => {
	const { siteSlug } = useParams<{ siteSlug: string }>();
	const dispatch = useDispatch<AppDispatch>();

	const isLoading = useSelector(
		(state: RootState) =>
			state.loading.loadingStates[AI_MODEL_FILE_LOADING_KEY],
	);

	const [isDragging, setIsDragging] = useState<boolean>(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const {
		control,
		reset,
		handleSubmit,
		setValue,
		watch,
		formState: { errors },
	} = useForm<IUploadAIModelFileForm>({
		resolver: yupResolver(UploadAIModelFileValidationSchema),
		defaultValues: {
			name: '',
			description: '',
			file: null,
		},
	});

	const selectedFile = watch('file');

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];

		if (file) {
			setValue('file', file, { shouldValidate: true });
			if (!watch('name')) {
				setValue('name', file.name);
			}
		}
	};

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		if (!isLoading) {
			setIsDragging(true);
		}
	};

	const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
	};

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);

		if (isLoading) return;

		const droppedFile = e.dataTransfer.files?.[0];
		if (droppedFile) {
			setValue('file', droppedFile, { shouldValidate: true });
			setValue('name', droppedFile.name);
		}
	};

	const handleRemoveFile = (e: React.MouseEvent) => {
		e.stopPropagation();
		setValue('file', null, { shouldValidate: true });
		setValue('name', '');

		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleSubmitClick = async (data: IUploadAIModelFileForm) => {
		if (!siteSlug || !data.file) return;

		try {
			const fileId = crypto.randomUUID();
			const buffer = await data.file.arrayBuffer();
			const hasher = getFileHasher();

			const result = await hasher.computeHashFromBuffer(
				buffer,
				data.file.name,
				data.file.size,
				data.file.type,
				fileId,
			);

			dispatch(
				uploadAIModelFile({
					siteSlug,
					aiModelId,
					request: {
						file: data.file,
						name: data.name,
						description: data.description,
						sha_256: result.hashes.sha256,
					},
					onSuccess: () => {
						snackbar('File uploaded successfully', 'success');
						onSuccess();
						handleDialogClose();
					},
				}),
			);
		} catch {
			snackbar('File could not be processed', 'error');
		}
	};

	const handleDialogClose = () => {
		if (isLoading) return;
		reset();
		onClose();
	};

	useEffect(() => {
		if (isOpen) {
			reset();
			setIsDragging(false);
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
		}
	}, [isOpen, reset]);

	useEffect(() => {
		return () => {
			terminateFileHasher();
		};
	}, []);

	return (
		<Dialog
			open={isOpen}
			PaperProps={{ sx: { width: 500 } }}
			onClose={(event, reason) => {
				if (
					isLoading &&
					(reason === 'backdropClick' || reason === 'escapeKeyDown')
				)
					return;
				handleDialogClose();
			}}
			disableEscapeKeyDown={isLoading}
		>
			<DialogTitle
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					pb: 0,
					mt: 1,
				}}
			>
				<Typography variant='h6' sx={{ fontWeight: 700 }}>
					Upload AI Model File
				</Typography>
				<IconButton onClick={handleDialogClose} disabled={isLoading}>
					<CloseIcon fontSize='small' />
				</IconButton>
			</DialogTitle>

			<DialogContent>
				<form
					onSubmit={handleSubmit(handleSubmitClick)}
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '1.5rem',
						paddingTop: '1rem',
					}}
				>
					<Box sx={{ display: 'flex', flexDirection: 'column' }}>
						<Box
							onDragOver={handleDragOver}
							onDragLeave={handleDragLeave}
							onDrop={handleDrop}
							onClick={() =>
								!isLoading && fileInputRef.current?.click()
							}
							sx={{
								border: '2px dashed',
								borderColor: errors.file
									? 'error.main'
									: isDragging
										? 'primary.main'
										: '#ccc',
								borderRadius: '8px',
								padding: 3,
								textAlign: 'center',
								cursor: isLoading ? 'default' : 'pointer',
								backgroundColor: isDragging
									? 'action.hover'
									: 'transparent',
								transition:
									'border-color 0.2s, background-color 0.2s',
								'&:hover': {
									backgroundColor: isLoading
										? 'transparent'
										: 'action.hover',
								},
							}}
						>
							<input
								ref={fileInputRef}
								type='file'
								style={{ display: 'none' }}
								onChange={handleFileChange}
								disabled={isLoading}
							/>

							{selectedFile ? (
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										gap: 2,
									}}
								>
									<InsertDriveFileOutlinedIcon color='action' />
									<Box
										sx={{
											textAlign: 'left',
											flex: 1,
											minWidth: 0,
										}}
									>
										<Typography
											variant='body2'
											sx={{
												fontWeight: 500,
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
											}}
										>
											{selectedFile.name}
										</Typography>
										<Typography
											variant='caption'
											color='text.secondary'
										>
											{(
												selectedFile.size /
												1024 /
												1024
											).toFixed(1)}{' '}
											MB
										</Typography>
									</Box>
									<IconButton
										size='small'
										onClick={handleRemoveFile}
										disabled={isLoading}
									>
										<CloseIcon fontSize='small' />
									</IconButton>
								</Box>
							) : (
								<Box>
									<CloudUploadOutlinedIcon
										sx={{
											fontSize: 40,
											color: 'text.secondary',
											mb: 1,
										}}
									/>
									<Typography
										variant='body2'
										color='text.secondary'
									>
										Drag & drop file here or click to browse
									</Typography>
								</Box>
							)}
						</Box>
						{errors.file && (
							<Typography
								variant='caption'
								color='error'
								sx={{ mt: 0.5, ml: 1 }}
							>
								{errors.file.message}
							</Typography>
						)}
					</Box>

					<Controller
						name='name'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='Name'
								variant='outlined'
								fullWidth
								error={!!errors.name}
								helperText={errors.name?.message}
								disabled={isLoading}
							/>
						)}
					/>

					<Controller
						name='description'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='Description'
								variant='outlined'
								fullWidth
								multiline
								rows={2}
								error={!!errors.description}
								helperText={errors.description?.message}
								disabled={isLoading}
							/>
						)}
					/>

					<Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
						<PrimaryButton
							type='submit'
							variant='contained'
							fullWidth
							disabled={isLoading}
							isLoading={isLoading}
						>
							UPLOAD
						</PrimaryButton>
						<Button
							variant='outlined'
							color='error'
							fullWidth
							onClick={handleDialogClose}
							disabled={isLoading}
						>
							CANCEL
						</Button>
					</Box>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default UploadAIModelFileModal;
